import React, { useCallback, useEffect, useState } from 'react';
import { Spinner } from 'react-bootstrap';
import PauseIcon from '@material-ui/icons/Pause';
import PlayArrowIcon from '@material-ui/icons/PlayArrow';
import ReplayIcon from '@material-ui/icons/Replay';
import ClockOutlineIcon from 'mdi-react/ClockOutlineIcon';
import {
  formatPositionRefreshCountdown,
  formatRefreshCountdown,
  getLastUpdatedLabel,
  getRemainingRefreshSeconds
} from '../utils/formatters';
import { refreshIntervalSeconds } from '../utils/constants';
import {
  HistoryActions,
  HistoryBadge,
  HistoryButton,
  HistoryIconWrap,
  HistoryIndicatorWrap,
  HistoryRefreshButton,
  HistoryTimestamp,
  PauseIconWrap
} from '../DisponibilidadeFrota-styled';

const RefreshIndicator = ({ onRefresh, loading = false, lastUpdatedAt = null, initialPaused = false }) => {
  const [refreshCountdown, setRefreshCountdown] = useState(refreshIntervalSeconds);
  const [paused, setPaused] = useState(initialPaused);
  const lastUpdatedLabel = getLastUpdatedLabel({ loading, lastUpdatedAt });

  const handleManualRefresh = useCallback(() => {
    if (loading) return;

    setRefreshCountdown(refreshIntervalSeconds);
    onRefresh?.();
  }, [loading, onRefresh]);

  useEffect(() => {
    if (loading) return;

    setRefreshCountdown(getRemainingRefreshSeconds(lastUpdatedAt));
  }, [lastUpdatedAt, loading]);

  useEffect(() => {
    if (paused || loading) return undefined;

    const intervalId = setInterval(() => {
      setRefreshCountdown(prev => Math.max(prev - 1, 0));
    }, 1000);

    return () => clearInterval(intervalId);
  }, [loading, paused]);

  useEffect(() => {
    if (paused || loading || refreshCountdown > 0) return;

    setRefreshCountdown(refreshIntervalSeconds);
    onRefresh?.();
  }, [loading, onRefresh, paused, refreshCountdown]);

  return (
    <HistoryIndicatorWrap>
      <HistoryActions>
        <HistoryButton type="button" aria-label="Atualizacao automatica" onClick={() => setPaused(prev => !prev)}>
          <HistoryIconWrap>
            <ClockOutlineIcon size={18} />
          </HistoryIconWrap>
          <PauseIconWrap $paused={paused}>
            {paused ? <PlayArrowIcon style={{ fontSize: 14 }} /> : <PauseIcon style={{ fontSize: 14 }} />}
          </PauseIconWrap>
          <HistoryBadge>
            {loading ? <Spinner animation="border" size="sm" /> : formatRefreshCountdown(refreshCountdown)}
          </HistoryBadge>
        </HistoryButton>
        <HistoryRefreshButton
          type="button"
          aria-label="Atualizar agora"
          title="Atualizar agora"
          onClick={handleManualRefresh}
          disabled={loading}
        >
          {loading ? <Spinner animation="border" size="sm" /> : <ReplayIcon style={{ fontSize: 18 }} />}
        </HistoryRefreshButton>
      </HistoryActions>
      <HistoryTimestamp title={lastUpdatedLabel}>{lastUpdatedLabel}</HistoryTimestamp>
    </HistoryIndicatorWrap>
  );
};

export const ApiRefreshIndicator = ({ enabled, onRefresh, loading = false, lastUpdatedAt = null, initialPaused = false }) => {
  if (!enabled) return null;

  return <RefreshIndicator onRefresh={onRefresh} loading={loading} lastUpdatedAt={lastUpdatedAt} initialPaused={initialPaused} />;
};

export const PositionRefreshIndicator = ({
  enabled,
  onRefresh,
  loading = false,
  lastUpdatedAt = null,
  paused = false,
  onTogglePaused,
  remainingMs = 0
}) => {
  if (!enabled) return null;

  const lastUpdatedLabel = getLastUpdatedLabel({ loading, lastUpdatedAt });

  return (
    <HistoryIndicatorWrap>
      <HistoryActions>
        <HistoryButton
          type="button"
          aria-label="Atualizacao automatica das posicoes"
          onClick={() => onTogglePaused?.()}
        >
          <HistoryIconWrap>
            <ClockOutlineIcon size={18} />
          </HistoryIconWrap>
          <PauseIconWrap $paused={paused}>
            {paused ? <PlayArrowIcon style={{ fontSize: 14 }} /> : <PauseIcon style={{ fontSize: 14 }} />}
          </PauseIconWrap>
          <HistoryBadge>
            {loading ? <Spinner animation="border" size="sm" /> : formatPositionRefreshCountdown(remainingMs)}
          </HistoryBadge>
        </HistoryButton>
        <HistoryRefreshButton
          type="button"
          aria-label="Atualizar posicoes agora"
          title="Atualizar posicoes agora"
          onClick={onRefresh}
          disabled={loading}
        >
          {loading ? <Spinner animation="border" size="sm" /> : <ReplayIcon style={{ fontSize: 18 }} />}
        </HistoryRefreshButton>
      </HistoryActions>
      <HistoryTimestamp title={lastUpdatedLabel}>{lastUpdatedLabel}</HistoryTimestamp>
    </HistoryIndicatorWrap>
  );
};

export default RefreshIndicator;
